"use client"

import { create } from "zustand"

interface SidebarState {
  // Desktop state (computed on init, not persisted)
  isCollapsed: boolean
  toolDrawerOpen: boolean

  // Mobile state (ephemeral, not persisted)
  leftSidebarOpen: boolean
  rightPanelOpen: boolean // legacy - kept for backward compat

  toolDrawerContent: "feed" | "brain" | null

  // Actions
  toggleCollapsed: () => void
  setCollapsed: (collapsed: boolean) => void
  toggleLeftSidebar: () => void
  closeLeftSidebar: () => void
  toggleRightPanel: () => void
  closeRightPanel: () => void

  toggleToolDrawer: () => void
  openToolDrawer: (content: "feed" | "brain") => void
  closeToolDrawer: () => void
  setToolDrawerContent: (content: "feed" | "brain" | null) => void

  closeAll: () => void
}

function hasSeenWorkspaceLayout(): boolean {
  if (typeof window === "undefined") return true // SSR: assume returning user (conservative default)
  try {
    const value = localStorage.getItem("logosiq.ui.didSeeWorkspaceLayout")
    return value === "true"
  } catch {
    return true // If localStorage unavailable, assume returning user
  }
}

function markWorkspaceLayoutSeen(): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem("logosiq.ui.didSeeWorkspaceLayout", "true")
    console.log("[v0] Marked workspace layout as seen")
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

function computeAdvisorDefaults(): { isCollapsed: boolean; toolDrawerOpen: boolean } {
  // This runs synchronously on store creation (client-side only)
  if (typeof window === "undefined") {
    // SSR: default to closed (no flash, hydration-safe)
    return { isCollapsed: true, toolDrawerOpen: false }
  }

  // Client: Check if user has seen workspace layout
  const hasSeenLayout = hasSeenWorkspaceLayout()

  if (!hasSeenLayout) {
    // First post-onboarding visit: orientation mode (both open)
    console.log("[v0] Advisor defaults: first post-onboarding visit - orientation mode (panels open)")
    // Mark as seen immediately to prevent re-triggering
    markWorkspaceLayoutSeen()
    return { isCollapsed: false, toolDrawerOpen: true }
  } else {
    // Returning session: focus mode (both closed)
    console.log("[v0] Advisor defaults: returning session - focus mode (panels closed)")
    return { isCollapsed: true, toolDrawerOpen: false }
  }
}

const advisorDefaults =
  typeof window !== "undefined" ? computeAdvisorDefaults() : { isCollapsed: true, toolDrawerOpen: false }

export const useSidebarStore = create<SidebarState>((set) => ({
  isCollapsed: advisorDefaults.isCollapsed,
  toolDrawerOpen: advisorDefaults.toolDrawerOpen,
  leftSidebarOpen: false,
  rightPanelOpen: false,
  toolDrawerContent: null,

  toggleCollapsed: () => set((state) => ({ isCollapsed: !state.isCollapsed })),
  setCollapsed: (collapsed) => set({ isCollapsed: collapsed }),
  toggleLeftSidebar: () => set((state) => ({ leftSidebarOpen: !state.leftSidebarOpen })),
  closeLeftSidebar: () => set({ leftSidebarOpen: false }),
  toggleRightPanel: () => set((state) => ({ rightPanelOpen: !state.rightPanelOpen })),
  closeRightPanel: () => set({ rightPanelOpen: false }),

  toggleToolDrawer: () => set((state) => ({ toolDrawerOpen: !state.toolDrawerOpen })),
  openToolDrawer: (content) => set({ toolDrawerOpen: true, toolDrawerContent: content }),
  closeToolDrawer: () => set({ toolDrawerOpen: false }),
  setToolDrawerContent: (content) => set({ toolDrawerContent: content }),

  closeAll: () => set({ leftSidebarOpen: false, rightPanelOpen: false, toolDrawerOpen: false }),
}))
